import { useState, useEffect, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import moment from "moment-timezone";
import Loader from "../layout/Loader";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import { alertContext } from "../contexts/AlertProvider";
import Alert from "../Alert";
import TruncatedTd from "../layout/TruncatedTd";
import MerchantLogo from "../layout/MerchantLogo";
import TransactionTable from "./TransactionTable";
import APIKeys from "./APIKeys";
import OrdableBranchKeys from "./OrdableBranchKeys";
import PermissionGate from "../layout/PermissionGate";
import PrecisedAddressMap from "../layout/PrecisedAddressMap";
import WalletBalance from "./details/wallet/WalletBalance";
import socket from "../socket/socket";
import MerchantAPIKey from "./MerchantAPIKey";
import normalize from "../utils/NormalizeSelect";
import OrderScanRestriction from "./details/OrderScanRestriction";
import PreferredDriverSetting from "./details/PreferredDriverSetting";
import CreditPanel from "./details/credit/CreditPanel";

const MerchantDetails = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({});
  const [branches, setBranches] = useState([]);
  const [areas, setAreas] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [streets, setStreets] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [licenceSize, setLicenceSize] = useState(0);
  const [contractSize, setContractSize] = useState(0);
  const [signatorySize, setSignatorySize] = useState(0);
  const [articleSize, setArticleSize] = useState(0);
  const [popup, setPopup] = useState("");
  const [branch, setBranch] = useState({});
  const [contact, setContact] = useState({});
  const [error, setError] = useState({});
  const [employees, setEmployees] = useState([]);
  const [employee, setEmployee] = useState({});
  const [orderScanRestriction, setOrderScanRestriction] = useState(true);
  const [showTransaction, setShowTransaction] = useState(false);
  const [creditRefreshKey, setCreditRefreshKey] = useState(0);
  const bumpCreditRefresh = () => setCreditRefreshKey((k) => k + 1);
  const [_, setAlertConfig] = useContext(alertContext);
  const { id } = useParams();

  const token = localStorage.getItem("token");

  const fetchMerchantDetails = () => {
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + `merchant-details?id=${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(async (res) => {
        if (res.data.code === 1) {
          setData(res.data.data);
          const scanRestrictionExempted = res?.data?.data?.scanRestrictionExempted;
          if (scanRestrictionExempted === true || scanRestrictionExempted === false) {
            setOrderScanRestriction(!scanRestrictionExempted);
          }
          setLicenceSize(
            await getFileSize(
              process.env.REACT_APP_API_PUBLIC_URL +
              res?.data?.data?.licensePhoto,
            ),
          );
          setContractSize(
            await getFileSize(
              process.env.REACT_APP_API_PUBLIC_URL +
              res?.data?.data?.contractPhoto,
            ),
          );
          setSignatorySize(
            await getFileSize(
              process.env.REACT_APP_API_PUBLIC_URL +
              res?.data?.data?.signatoryDocument,
            ),
          );
          setArticleSize(
            await getFileSize(
              process.env.REACT_APP_API_PUBLIC_URL +
              res?.data?.data?.articlesOfAssociation,
            ),
          );
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      })
      .catch(console.error);
  };
  const getFileSize = async (url) => {
    const size = axios
      .head(url)
      .then((res) => {
        let fileSize = res.headers.get("Content-Length");
        if (fileSize) {
          fileSize = (parseInt(fileSize, 10) / 1024).toFixed(2);
        } else {
          fileSize = 0;
        }
        return fileSize;
      })
      .catch((error) => {
        console.log(error);
        return 0;
      });
    return size;
  };
  const fetchBranches = () => {
    axios
      .get(
        process.env.REACT_APP_API_ENDPOINT + `all-branches?merchantId=${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setBranches(res.data.data);
        }
      })
      .catch(console.error);
  };
  const fetchContacts = () => {
    axios
      .get(
        process.env.REACT_APP_API_ENDPOINT + `all-contacts?merchantId=${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setContacts(res.data.data);
        }
      })
      .catch(console.error);
  };
  const fetchEmployees = () => {
    axios
      .get(
        process.env.REACT_APP_API_ENDPOINT + `all-employees?merchantId=${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setEmployees(res.data.data);
        }
      })
      .catch(console.error);
  };
  const openPopup = (action, data) => {
    switch (action) {
      case "add-branch":
        setBranch(data);
        break;
      case "edit-branch":
        setBranch(data);
        fetchBlockList(data?.area);
        fetchStreetList(data?.block, data?.area);
        break;
      case "delete-branch":
        setBranch(data);
        break;
      case "add-contact":
        setContact(data);
        break;
      case "edit-contact":
        setContact(data);
        break;
      case "delete-contact":
        setContact(data);
        break;
      case "add-employee":
        setEmployee(data);
        break;
      case "edit-employee":
        setEmployee(data);
        break;
      case "reset-password-employee":
        setEmployee(data);
        break;
      case "change-employee-password":
        setEmployee({
          _id: data?._id,
          merchantId: data?.merchantId,
        });
        break;
      case "delete-employee":
        setEmployee(data);
        break;
      default:
        break;
    }
    setPopup(action);
  };
  const addBranch = () => {
    const errors = {};
    if (branch.name === undefined || branch.name === "") {
      errors["name"] = "Please Enter Branch Name";
    }
    if (branch.phone === undefined || branch.phone === "") {
      errors["phone"] = "Please Enter Phone No";
    }
    if (
      typeof branch?.area !== "object" ||
      Object.keys(branch?.area).length === 0
    ) {
      errors["area"] = "Please Select Area";
    }
    if (
      typeof branch?.block !== "object" ||
      Object.keys(branch?.block).length === 0
    ) {
      errors["block"] = "Please Select Block";
    }
    if (
      typeof branch?.street !== "object" ||
      Object.keys(branch?.street).length === 0
    ) {
      errors["street"] = "Please Select Street";
    }
    if (branch.building === undefined || branch.building === "") {
      errors["building"] = "Please Enter Building Name";
    }
    if (branch.jadda === undefined || branch.jadda === "") {
      errors["jadda"] = "Please Enter Jadda";
    }
    if (branch.apartment === undefined || branch.apartment === "") {
      errors["apartment"] = "Please Enter Apartment Name";
    }
    if (branch.floor === undefined || branch.floor === "") {
      errors["floor"] = "Please Enter Floor";
    }
    if (
      branch.latitude !== undefined &&
      branch.latitude !== null &&
      String(branch.latitude).trim() !== "" &&
      isNaN(parseFloat(branch.latitude))
    ) {
      errors["latitude"] = "Please Enter Latitude";
    }
    if (
      branch.longitude !== undefined &&
      branch.longitude !== null &&
      String(branch.longitude).trim() !== "" &&
      isNaN(parseFloat(branch.longitude))
    ) {
      errors["longitude"] = "Please Enter Longitude";
    }

    setError({ ...errors });
    if (
      errors.name === undefined &&
      errors.phone === undefined &&
      errors.area === undefined &&
      errors.block === undefined &&
      errors.street === undefined &&
      errors.building === undefined &&
      errors.jadda === undefined &&
      errors.apartment === undefined &&
      errors.floor === undefined &&
      errors.latitude === undefined &&
      errors.longitude === undefined
    ) {
      axios
        .post(
          process.env.REACT_APP_API_ENDPOINT + "create-branch",
          { ...branch, merchantId: id },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .then((res) => {
          if (res) {
            if (res.data.code === 0) {
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "danger",
              });
            }
            if (res.data.code === 1) {
              setPopup("");
              setBranch({});
              setError({});
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "success",
              });
              fetchBranches();
            }
          }
        })
        .catch(console.error);
    }
  };
  const editBranch = () => {
    const errors = {};
    if (branch.name === undefined || branch.name === "") {
      errors["name"] = "Please Enter Branch Name";
    }
    if (branch.phone === undefined || branch.phone === "") {
      errors["phone"] = "Please Enter Phone No";
    }
    if (
      typeof branch?.area !== "object" ||
      Object.keys(branch?.area).length === 0
    ) {
      errors["area"] = "Please Select Area";
    }
    if (
      typeof branch?.block !== "object" ||
      Object.keys(branch?.block).length === 0
    ) {
      errors["block"] = "Please Select Block";
    }
    if (
      typeof branch?.street !== "object" ||
      Object.keys(branch?.street).length === 0
    ) {
      errors["street"] = "Please Select Street";
    }
    if (branch.building === undefined || branch.building === "") {
      errors["building"] = "Please Enter Building Name";
    }
    if (branch.jadda === undefined || branch.jadda === "") {
      errors["jadda"] = "Please Enter Jadda";
    }
    if (branch.apartment === undefined || branch.apartment === "") {
      errors["apartment"] = "Please Enter Apartment Name";
    }
    if (branch.floor === undefined || branch.floor === "") {
      errors["floor"] = "Please Enter Floor";
    }
    if (
      branch.latitude !== undefined &&
      branch.latitude !== null &&
      String(branch.latitude).trim() !== "" &&
      isNaN(parseFloat(branch.latitude))
    ) {
      errors["latitude"] = "Please Enter Latitude";
    }
    if (
      branch.longitude !== undefined &&
      branch.longitude !== null &&
      String(branch.longitude).trim() !== "" &&
      isNaN(parseFloat(branch.longitude))
    ) {
      errors["longitude"] = "Please Enter Longitude";
    }

    setError({ ...errors });
    if (
      errors.name === undefined &&
      errors.phone === undefined &&
      errors.area === undefined &&
      errors.block === undefined &&
      errors.street === undefined &&
      errors.building === undefined &&
      errors.jadda === undefined &&
      errors.apartment === undefined &&
      errors.floor === undefined &&
      errors.latitude === undefined &&
      errors.longitude === undefined
    ) {
      axios
        .post(process.env.REACT_APP_API_ENDPOINT + "edit-branch", branch, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          if (res) {
            if (res.data.code === 0) {
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "danger",
              });
            }
            if (res.data.code === 1) {
              setPopup("");
              setBranch({});
              setError({});
              setBlocks([]);
              setStreets([]);
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "success",
              });
              fetchBranches();
            }
          }
        })
        .catch(console.error);
    }
  };
  const deleteBranch = () => {
    axios
      .post(process.env.REACT_APP_API_ENDPOINT + "delete-branch", branch, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res) {
          if (res.data.code === 0) {
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "danger",
            });
          }
          if (res.data.code === 1) {
            setPopup("");
            setBranch({});
            setError({});
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "success",
            });
            fetchBranches();
          }
        }
      })
      .catch(console.error);
  };
  const addContact = () => {
    const errors = {};
    if (contact.title === undefined || contact.title === "") {
      errors["title"] = "Please Enter Title";
    }
    if (contact.name === undefined || contact.name === "") {
      errors["name"] = "Please Enter Name";
    }
    if (contact.email === undefined || contact.email === "") {
      errors["email"] = "Please Enter Email";
    }
    if (contact.phone === undefined || contact.phone === "") {
      errors["phone"] = "Please Enter Phone No";
    }
    if (contact.officePhone === undefined || contact.officePhone === "") {
      errors["officePhone"] = "Please Enter Office Phone No";
    }

    setError({ ...errors });
    if (
      errors.title === undefined &&
      errors.name === undefined &&
      errors.email === undefined &&
      errors.phone === undefined &&
      errors.officePhone === undefined
    ) {
      axios
        .post(
          process.env.REACT_APP_API_ENDPOINT + "create-contact",
          { ...contact, merchantId: id },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .then((res) => {
          if (res) {
            if (res.data.code === 0) {
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "danger",
              });
            }
            if (res.data.code === 1) {
              setPopup("");
              setContact({});
              setError({});
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "success",
              });
              fetchContacts();
            }
          }
        })
        .catch(console.error);
    }
  };
  const editContact = () => {
    const errors = {};
    if (contact.title === undefined || contact.title === "") {
      errors["title"] = "Please Enter Title";
    }
    if (contact.name === undefined || contact.name === "") {
      errors["name"] = "Please Enter Name";
    }
    if (contact.email === undefined || contact.email === "") {
      errors["email"] = "Please Enter Email";
    }
    if (contact.phone === undefined || contact.phone === "") {
      errors["phone"] = "Please Enter Phone No";
    }
    if (contact.officePhone === undefined || contact.officePhone === "") {
      errors["officePhone"] = "Please Enter Office Phone No";
    }

    setError({ ...errors });
    if (
      errors.title === undefined &&
      errors.name === undefined &&
      errors.email === undefined &&
      errors.phone === undefined &&
      errors.officePhone === undefined
    ) {
      axios
        .post(process.env.REACT_APP_API_ENDPOINT + "edit-contact", contact, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          if (res) {
            if (res.data.code === 0) {
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "danger",
              });
            }
            if (res.data.code === 1) {
              setPopup("");
              setContact({});
              setError({});
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "success",
              });
              fetchContacts();
            }
          }
        })
        .catch(console.error);
    }
  };
  const deleteContact = () => {
    axios
      .post(process.env.REACT_APP_API_ENDPOINT + "delete-contact", contact, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res) {
          if (res.data.code === 0) {
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "danger",
            });
          }
          if (res.data.code === 1) {
            setPopup("");
            setContact({});
            setError({});
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "success",
            });
            fetchContacts();
          }
        }
      })
      .catch(console.error);
  };
  const addEmployee = () => {
    const errors = {};
    if (employee.name === undefined || employee.name === "") {
      errors["name"] = "Please Enter Name";
    }
    if (employee.email === undefined || employee.email === "") {
      errors["email"] = "Please Enter Email";
    }
    if (employee.role === undefined || employee.role === "") {
      errors["role"] = "Please Select Role";
    }
    if (employee.branchId === undefined || employee.branchId === "") {
      errors["branchId"] = "Please Select Branch";
    }

    setError({ ...errors });
    if (
      errors.name === undefined &&
      errors.email === undefined &&
      errors.role === undefined &&
      errors.branchId === undefined
    ) {
      axios
        .post(
          process.env.REACT_APP_API_ENDPOINT + "create-employee",
          { ...employee, merchantId: id, delivaryCompanyId: null },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .then((res) => {
          if (res) {
            if (res.data.code === 0) {
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "danger",
              });
            }
            if (res.data.code === 1) {
              setPopup("");
              setContact({});
              setError({});
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "success",
              });
              fetchEmployees();
            }
          }
        })
        .catch(console.error);
    }
  };
  const editEmployee = () => {
    const errors = {};
    if (employee.name === undefined || employee.name === "") {
      errors["name"] = "Please Enter Name";
    }
    if (employee.email === undefined || employee.email === "") {
      errors["email"] = "Please Enter Email";
    }
    if (employee.role === undefined || employee.role === "") {
      errors["role"] = "Please Select Role";
    }
    if (employee.branchId === undefined || employee.branchId === "") {
      errors["branchId"] = "Please Select Branch";
    }

    setError({ ...errors });
    if (
      errors.name === undefined &&
      errors.email === undefined &&
      errors.role === undefined &&
      errors.branchId === undefined
    ) {
      axios
        .post(process.env.REACT_APP_API_ENDPOINT + "edit-employee", employee, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          if (res) {
            if (res.data.code === 0) {
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "danger",
              });
            }
            if (res.data.code === 1) {
              setPopup("");
              setEmployee({});
              setError({});
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "success",
              });
              fetchEmployees();
            }
          }
        })
        .catch(console.error);
    }
  };
  const deleteEmployee = () => {
    axios
      .post(process.env.REACT_APP_API_ENDPOINT + "delete-employee", employee, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res) {
          if (res.data.code === 0) {
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "danger",
            });
          }
          if (res.data.code === 1) {
            setPopup("");
            setEmployee({});
            setError({});
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "success",
            });
            fetchEmployees();
          }
        }
      })
      .catch(console.error);
  };
  const resetPasswordEmployee = () => {
    axios
      .post(
        process.env.REACT_APP_API_ENDPOINT + "reset-password-employee",
        employee,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res) {
          if (res.data.code === 0) {
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "danger",
            });
          }
          if (res.data.code === 1) {
            setPopup("");
            setEmployee({});
            setError({});
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "success",
            });
            fetchEmployees();
          }
        }
      })
      .catch(console.error);
  };
  const changeEmployeePassword = () => {
    const errors = {};
    if (!employee.password?.trim()) {
      errors["password"] = "Please Enter New Password";
    }
    if (!employee.confirmPassword?.trim()) {
      errors["confirmPassword"] = "Please Confirm Password";
    }
    if (employee.password?.trim() !== employee.confirmPassword?.trim()) {
      errors["password"] = "Passwords did not match";
    }

    setError({ ...errors });
    if (errors.password === undefined && errors.confirmPassword === undefined) {
      axios
        .patch(
          `${process.env.REACT_APP_API_ENDPOINT}change-employee-password/${employee?._id}`,
          {
            merchantId: employee?.merchantId,
            password: employee?.password,
            confirmPassword: employee?.confirmPassword,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .then((res) => {
          if (res) {
            if (res.data.code === 0) {
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "danger",
              });
            }
            if (res.data.code === 1) {
              setPopup("");
              setEmployee({});
              setError({});
              setAlertConfig({
                show: true,
                message: res.data.message,
                type: "success",
              });
            }
          }
        })
        .catch(console.error);
    }
  };
  const fetchAreaList = () => {
    axios
      .get(`${process.env.REACT_APP_API_ENDPOINT}location/areas`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res.data.code === 1) {
          setAreas(res.data.data);
        }
      })
      .catch(console.error);
  };
  const fetchBlockList = (area) => {
    const areaName = encodeURIComponent(area?.value);
    axios
      .get(`${process.env.REACT_APP_API_ENDPOINT}location/blocks/${areaName}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res.data.code === 1) {
          setBlocks(res.data.data);
        }
      })
      .catch(console.error);
  };
  const fetchStreetList = (block, area = {}) => {
    const areaName = encodeURIComponent(branch?.area?.value ?? area?.value);
    const blockName = encodeURIComponent(block?.value);
    axios
      .get(
        `${process.env.REACT_APP_API_ENDPOINT}location/streets/${areaName}/${blockName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setStreets(res.data.data);
        }
      })
      .catch(console.error);
  };
  const changeMerchantOrderScanRestriction = (orderScanRestrictionStatus) => {
    if (orderScanRestrictionStatus !== true && orderScanRestrictionStatus !== false) {
      return;
    }
    axios
      .patch(`${process.env.REACT_APP_API_ENDPOINT}change-merchant-order-scan-restriction/${id}`, { status: !orderScanRestrictionStatus }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res) {
          if (res.data.code === 0) {
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "danger",
            });
          }
          if (res.data.code === 1) {
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "success",
            });
            fetchMerchantDetails();
          }
        }
      })
      .catch(console.error);
  };
  const saveMerchantPreferredDriverSetting = (settings) => {
    axios
      .patch(
        `${process.env.REACT_APP_API_ENDPOINT}update-merchant-preferred-driver-setting/${id}`,
        settings,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res) {
          if (res.data.code === 0) {
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "danger",
            });
          }
          if (res.data.code === 1) {
            setAlertConfig({
              show: true,
              message: res.data.message,
              type: "success",
            });
            fetchMerchantDetails();
          }
        }
      })
      .catch(console.error);
  };
  useEffect(() => {
    socket.on(`admin:ordersUpdate`, (data) => {
      if (data?.merchantId?.trim() !== id) return;
      fetchMerchantDetails();
    });
    return () => {
      socket.off(`admin:ordersUpdate`);
    };
  }, []);
  useEffect(() => {
    fetchMerchantDetails();
    fetchBranches();
    fetchContacts();
    fetchEmployees();
    fetchAreaList();
  }, []);

  return isLoading === true ? (
    <Loader />
  ) : (
    <div className="d-flex flex-column flex-root">
      <Alert />
      {/*begin::Page*/}
      <div className="page d-flex flex-row flex-column-fluid">
        {/*begin::Wrapper*/}
        <div
          className="wrapper d-flex flex-column flex-row-fluid"
          id="kt_wrapper"
        >
          <Header />
          <div className="app-body">
            <PermissionGate required={`view_merchant_details`}>
              <>
                <div className="toolbar py-3 py-lg-6" id="kt_toolbar">
                  {/*begin::Container*/}
                  <div
                    id="kt_toolbar_container"
                    className="container-xxl  d-flex flex-stack flex-wrap gap-2"
                  >
                    {/*begin::Page title*/}
                    <div className="page-title d-flex flex-column align-items-start me-3 py-2 py-lg-0 gap-2">
                      {/*begin::Title*/}
                      <h1 className="d-flex text-dark fw-bold m-0 fs-3">
                        Merchant
                      </h1>
                      {/*end::Title*/}
                    </div>
                    {/*end::Page title*/}
                    {/*begin::Actions*/}
                    <div className="d-flex align-items-center">
                      {/*begin::Edit Button*/}
                      <PermissionGate
                        required={`edit_merchant_details`}
                        fallback={<></>}
                      >
                        <Link
                          to={`/merchants/edit-merchant/${id}`}
                          className="ms-4 btn btn-color-success bg-body me-3"
                        >
                          <i className="bi-duotone bi-pen"></i>
                          Edit
                        </Link>
                      </PermissionGate>

                      {/*end::Edit Button*/}
                    </div>
                    {/*end::Actions*/}
                  </div>
                  {/*end::Container*/}
                </div>
                {/*end::Toolbar*/}

                {/*begin::Container*/}
                <div
                  id="kt_content_container"
                  className="d-flex flex-column-fluid align-items-start  container-xxl "
                >
                  {/*begin::Post*/}
                  <div className="content flex-row-fluid" id="kt_content">
                    <div className="row g-5 g-lg-10">
                      {/*begin::Card*/}
                      <div className="card">
                        {/*begin::Card header*/}
                        <div className="card-header border-0 pt-6">
                          {/*begin::Card title*/}
                          <div className="card-title"></div>
                          {/*begin::Card title*/}
                        </div>
                        {/*end::Card header*/}

                        {/*begin::Card body*/}
                        <div className="card-body py-4">
                          <div className="row">
                            <div className="col">
                              <div className="d-flex flex-wrap flex-sm-nowrap">
                                {/*begin: Pic*/}
                                <div className="me-7 mb-4">
                                  <div className="symbol symbol-100px symbol-lg-160px symbol-fixed position-relative">
                                    <MerchantLogo
                                      merchantLogo={data?.merchantLogo}
                                      merchantName={data?.merchantName}
                                      fallbackClassName="fs-3x"
                                    />
                                    <div
                                      className={`position-absolute translate-middle bottom-0 start-100 mb-6 bg-${data?.status === true
                                        ? "success"
                                        : "danger"
                                        } rounded-circle border border-4 border-body h-20px w-20px`}
                                    ></div>
                                  </div>
                                </div>
                                {/*end::Pic*/}

                                {/*begin::Info*/}
                                <div className="flex-grow-1">
                                  {/*begin::Title*/}
                                  <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                                    {/*begin::User*/}
                                    <div className="d-flex flex-column">
                                      {/*begin::Name*/}
                                      <div className="d-flex align-items-center mb-2">
                                        <a
                                          href="#"
                                          className="text-gray-900 text-hover-primary fs-2 fw-bold me-1"
                                        >
                                          {data?.merchantName}
                                        </a>
                                      </div>
                                      {/*end::Name*/}

                                      {/*begin::Info*/}
                                      <div className="d-flex flex-wrap fw-semibold fs-6 mb-4 mt-5 pe-2">
                                        <a
                                          href="#"
                                          className="d-flex align-items-center text-gray-400 me-5 mb-2"
                                        >
                                          Legal Name
                                        </a>
                                        <a
                                          href="#"
                                          className="d-flex align-items-center text-white-400 mb-2"
                                        >
                                          {data?.legalName}
                                        </a>
                                      </div>
                                      {/*end::Info*/}
                                    </div>
                                    {/*end::User*/}
                                  </div>
                                  {/*end::Title*/}
                                </div>
                                {/*end::Info*/}
                              </div>
                            </div>
                            <div className="col">
                              {/*begin::Legal Address*/}
                              <div className="fw-semibold fs-6 mb-4 mt-5 pe-2">
                                <a
                                  href="#"
                                  className="d-flex align-items-center text-gray-400 me-5 mb-2"
                                >
                                  Legal Address
                                </a>
                                <a
                                  href="#"
                                  className="d-flex align-items-center text-white-400 mb-2"
                                >
                                  {`${data?.legalArea?.value}, Block ${data?.legalBlock?.value}, ${data?.legalStreet?.value}`}
                                </a>
                              </div>
                              {/*end::Legal Address*/}
                              {/*begin::Billing Address*/}
                              <div className="fw-semibold fs-6 mb-4 mt-5 pe-2">
                                <a
                                  href="#"
                                  className="d-flex align-items-center text-gray-400 me-5 mb-2"
                                >
                                  Billing Address
                                </a>
                                <a
                                  href="#"
                                  className="d-flex align-items-center text-white-400 mb-2"
                                >
                                  {`${data?.billingArea?.value}, Block ${data?.billingBlock?.value}, ${data?.billingStreet?.value}`}
                                </a>
                              </div>
                              {/*end::Billing Address*/}
                            </div>
                            <div className="col">
                              {/*begin::Default Currency*/}
                              <div className="fw-semibold fs-6 mb-4 mt-5 pe-2">
                                <a
                                  href="#"
                                  className="d-flex align-items-center text-gray-400 me-5 mb-2"
                                >
                                  Default Currency
                                </a>
                                <a
                                  href="#"
                                  className="badge bg-success text-white-400 pt-3"
                                >
                                  <div className="d-flex align-items-center text-white-400 text-light mb-2">
                                    {data?.currency}
                                  </div>
                                </a>
                              </div>
                              {/*end::Default Currency*/}
                              {/*begin::Licence Expiary*/}
                              <div className="fw-semibold fs-6 mb-4 mt-5 pe-2">
                                <a
                                  href="#"
                                  className="d-flex align-items-center text-gray-400 me-5 mb-2"
                                >
                                  Licence Expiary
                                </a>
                                <a
                                  href="#"
                                  className="d-flex align-items-center text-white-400 mb-2"
                                >
                                  {moment(data?.licenceExpiary, "DD-MM-YYYY")
                                    .tz(process.env.REACT_APP_TIMEZONE)
                                    .format("Do MMM, YYYY")}
                                </a>
                              </div>
                              {/*end::Licence Expiary*/}
                            </div>
                          </div>
                          <div className="overflow-auto pb-5">
                            <div className="d-flex align-items-center border border-dashed border-gray-300 rounded min-w-700px p-5">
                              {/*begin::Licence*/}
                              <div className="d-flex flex-aligns-center pe-10 pe-lg-20">
                                {/*begin::Icon*/}
                                <img
                                  alt=""
                                  className="w-40px me-3"
                                  src={
                                    process.env.REACT_APP_PUBLIC_URL +
                                    "icons/certificate.png"
                                  }
                                />
                                {/*end::Icon*/}

                                {/*begin::Licence*/}
                                <div className="ms-1 fw-semibold">
                                  {/*begin::Desc*/}
                                  <a
                                    target="_blank"
                                    href={
                                      process.env.REACT_APP_API_PUBLIC_URL +
                                      data?.licensePhoto
                                    }
                                    className="fs-6 text-hover-primary fw-bold"
                                    download
                                  >
                                    Merchant Licence
                                  </a>
                                  {/*end::Desc*/}
                                  {/*begin::Number*/}
                                  <div className="text-gray-400">
                                    {licenceSize} KB
                                  </div>
                                  {/*end::Number*/}
                                </div>
                                {/*begin::Licence*/}
                              </div>
                              {/*end::Licence*/}

                              {/*begin::Contract*/}
                              <div className="d-flex flex-aligns-center pe-10 pe-lg-20">
                                {/*begin::Icon*/}
                                <img
                                  alt=""
                                  className="w-40px me-3"
                                  src={
                                    process.env.REACT_APP_PUBLIC_URL +
                                    "icons/compliant.png"
                                  }
                                />
                                {/*end::Icon*/}

                                {/*begin::Licence*/}
                                <div className="ms-1 fw-semibold">
                                  {/*begin::Desc*/}
                                  <a
                                    target="_blank"
                                    href={
                                      process.env.REACT_APP_API_PUBLIC_URL +
                                      data?.contractPhoto
                                    }
                                    className="fs-6 text-hover-primary fw-bold"
                                    download
                                  >
                                    Contract Paper
                                  </a>
                                  {/*end::Desc*/}
                                  {/*begin::Number*/}
                                  <div className="text-gray-400">
                                    {contractSize} KB
                                  </div>
                                  {/*end::Number*/}
                                </div>
                                {/*begin::Licence*/}
                              </div>
                              {/*end::Contract*/}

                              {/*begin::Signatory*/}
                              <div className="d-flex flex-aligns-center pe-10 pe-lg-20">
                                <img
                                  alt=""
                                  className="w-40px me-3"
                                  src={
                                    process.env.REACT_APP_PUBLIC_URL +
                                    "icons/contract.png"
                                  }
                                />
                                <div className="ms-1 fw-semibold">
                                  <a
                                    target="_blank"
                                    href={
                                      process.env.REACT_APP_API_PUBLIC_URL +
                                      data?.signatoryDocument
                                    }
                                    className="fs-6 text-hover-primary fw-bold"
                                    download
                                  >
                                    Signatory Document
                                  </a>
                                  <div className="text-gray-400">
                                    {signatorySize} KB
                                  </div>
                                </div>
                              </div>
                              {/*end::Signatory*/}

                              {/*begin::Article*/}
                              <div className="d-flex flex-aligns-center pe-10 pe-lg-20">
                                <img
                                  alt=""
                                  className="w-40px me-3"
                                  src={
                                    process.env.REACT_APP_PUBLIC_URL +
                                    "icons/paper.png"
                                  }
                                />
                                <div className="ms-1 fw-semibold">
                                  <a
                                    target="_blank"
                                    href={
                                      process.env.REACT_APP_API_PUBLIC_URL +
                                      data?.articlesOfAssociation
                                    }
                                    className="fs-6 text-hover-primary fw-bold"
                                    download
                                  >
                                    Articles of Association{" "}
                                  </a>
                                  <div className="text-gray-400">
                                    {articleSize} KB
                                  </div>
                                </div>
                              </div>
                              {/*end::Article*/}
                            </div>
                          </div>
                        </div>
                        {/*end::Card body*/}
                      </div>
                      {/*end::Card*/}
                    </div>
                    {/*begin::Row*/}
                    <div className="row g-5 g-lg-10 mt-4">
                      {/*begin::Col*/}
                      <div className="col ps-0 mb-xl-10" id="loadBranches">
                        <h1 className="d-flex text-dark fw-bold m-0 fs-3 mb-5">
                          Branches
                        </h1>
                        {/*begin::Tables Widget 3*/}
                        <PermissionGate required={`view_merchant_branch`}>
                          <div className="card">
                            {/*begin::Body*/}
                            <div className="card-body">
                              {branches && branches.length > 0 ? (
                                <div className="table-responsive">
                                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                                    <thead>
                                      <tr className="text-muted fw-bold fs-7 text-uppercase gs-0">
                                        <th className="text-center min-w-100px">
                                          Name
                                        </th>
                                        <th className="text-center min-w-100px">
                                          Phone
                                        </th>
                                        <th className="text-center min-w-100px">
                                          Address
                                        </th>
                                        <th className="text-center min-w-100px">
                                          Video
                                        </th>
                                        <th className="text-center min-w-100px">
                                          Actions
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody
                                      className="text-gray-600 fw-semibold"
                                      id="filterDataWrap"
                                    >
                                      {branches &&
                                        branches.map((branch, index) => {
                                          return (
                                            <tr
                                              key={index}
                                              className="text-center"
                                            >
                                              <TruncatedTd length={8}>
                                                {branch?.name}
                                              </TruncatedTd>
                                              <TruncatedTd length={8}>
                                                {branch?.phone}
                                              </TruncatedTd>
                                              <TruncatedTd length={8}>
                                                {`${branch?.area?.value}, ${branch?.block?.value}, ${branch?.street?.value}`}
                                              </TruncatedTd>
                                              <td className="text-center">
                                                {branch?.tutorialVideoUrl ? (
                                                  <span className="badge bg-success">
                                                    Added
                                                  </span>
                                                ) : (
                                                  <span className="badge bg-light text-muted">
                                                    None
                                                  </span>
                                                )}
                                              </td>
                                              <td className="">
                                                <div className="btn-group">
                                                  {/*begin::Edit*/}
                                                  <PermissionGate
                                                    required={`edit_merchant_branch`}
                                                    fallback={<></>}
                                                  >
                                                    <button
                                                      onClick={() => {
                                                        openPopup(
                                                          "edit-branch",
                                                          branch,
                                                        );
                                                      }}
                                                      className="btn px-2 btn-outline btn-primary btn-color-white btn-sm"
                                                      title="Edit Branch"
                                                    >
                                                      <i className="bi-duotone bi-pen fs-5"></i>
                                                    </button>
                                                  </PermissionGate>

                                                  {/*end::Edit*/}
                                                  {/*begin::Delete*/}
                                                  <PermissionGate
                                                    required={`delete_merchant_branch`}
                                                    fallback={<></>}
                                                  >
                                                    <button
                                                      onClick={() => {
                                                        openPopup(
                                                          "delete-branch",
                                                          branch,
                                                        );
                                                      }}
                                                      className="btn px-2 btn-outline btn-danger btn-color-white btn-sm"
                                                      title="Delete Branch"
                                                    >
                                                      <i className="bi-duotone bi-trash fs-5"></i>
                                                    </button>
                                                  </PermissionGate>

                                                  {/*end::Delete*/}
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-center w-100">
                                  <img
                                    className="w-25 mx-auto py-20"
                                    src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/misc/no-content.png`}
                                    alt="no-content"
                                  />
                                </div>
                              )}
                              <PermissionGate
                                required={`add_merchant_branch`}
                                fallback={<></>}
                              >
                                <button
                                  onClick={() => {
                                    openPopup("add-branch", {});
                                  }}
                                  className="btn btn-success btn-sm w-100 me-3"
                                >
                                  Add
                                </button>
                              </PermissionGate>
                            </div>
                            {/*begin::Body*/}
                          </div>
                        </PermissionGate>
                        {/*end::Tables Widget 3*/}
                        {/*begin::Modal - Add Branches*/}
                        <div
                          className={
                            popup === "add-branch"
                              ? "modal fade show d-block"
                              : "modal fade"
                          }
                          tabIndex="-1"
                          id="addBranchesModal"
                          aria-hidden="true"
                        >
                          {/*begin::Modal dialog*/}
                          <div className="modal-dialog modal-dialog-centered modal-xl">
                            {/*begin::Modal content*/}
                            <div className="modal-content rounded">
                              {/*begin::Modal header*/}
                              <div className="modal-header">
                                {/*begin::Close*/}
                                <h4>Add Branch</h4>
                                {/*end::Close*/}
                              </div>
                              {/*end::Modal header*/}

                              {/*begin::Modal body*/}
                              <div className="modal-body p-4">
                                <div className="row">
                                  <div className="col-md-6">
                                    <div className="row p-5">
                                      <div className="col-md-6">
                                        <label className="form-label">
                                          Name
                                        </label>
                                        <input
                                          value={branch?.name ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              name: e.target.value,
                                            });
                                            setError({ ...error, name: "" });
                                          }}
                                          autoComplete="off"
                                          id="branch_name"
                                          className="form-control"
                                          placeholder="Enter Branch Name"
                                        />
                                        {error.name !== undefined &&
                                          error.name !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.name}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6">
                                        <label className="form-label">
                                          Phone
                                        </label>
                                        <input
                                          value={branch?.phone ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              phone: e.target.value,
                                            });
                                            setError({ ...error, phone: "" });
                                          }}
                                          autoComplete="off"
                                          id="branch_phone"
                                          className="form-control"
                                          placeholder="Enter Branch Phone"
                                        />
                                        {error.phone !== undefined &&
                                          error.phone !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.phone}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Area
                                        </label>
                                        <Select
                                          className="react-select-styled react-select-bordered"
                                          classNamePrefix="react-select"
                                          value={
                                            branch?.area
                                              ? {
                                                label: branch?.area?.value,
                                                value: branch?.area?.value,
                                              }
                                              : null
                                          }
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              area: e?.value,
                                            });
                                            setError({ ...error, area: "" });
                                            setBranch(
                                              ({ block, street, ...rest }) =>
                                                rest,
                                            );
                                            fetchBlockList(e?.value);
                                          }}
                                          options={areas?.map((item) => {
                                            return {
                                              value: item,
                                              label: item?.value,
                                            };
                                          })}
                                          filterOption={(option, input) => {
                                            return normalize(option.label).includes(normalize(input));
                                          }}
                                          placeholder="Select area"
                                        />
                                        {error.area !== undefined &&
                                          error.area !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.area}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Block
                                        </label>
                                        <Select
                                          className="react-select-styled react-select-bordered"
                                          classNamePrefix="react-select"
                                          value={
                                            branch?.block
                                              ? {
                                                label: branch?.block?.value,
                                                value: branch?.block?.value,
                                              }
                                              : null
                                          }
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              block: e?.value,
                                            });
                                            setError({ ...error, block: "" });
                                            setBranch(
                                              ({ street, ...rest }) => rest,
                                            );
                                            fetchStreetList(e?.value);
                                          }}
                                          options={blocks?.map((item) => {
                                            return {
                                              value: item,
                                              label: item?.value,
                                            };
                                          })}
                                          filterOption={(option, input) => {
                                            return normalize(option.label).includes(normalize(input));
                                          }}
                                          placeholder="Select block"
                                        />
                                        {error.block !== undefined &&
                                          error.block !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.block}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-12 mt-3">
                                        <label className="form-label">
                                          Street
                                        </label>
                                        <Select
                                          className="react-select-styled react-select-bordered"
                                          classNamePrefix="react-select"
                                          value={
                                            branch?.street
                                              ? {
                                                label: branch?.street?.value,
                                                value: branch?.street?.value,
                                              }
                                              : null
                                          }
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              street: e?.value,
                                            });
                                            setError({ ...error, street: "" });
                                          }}
                                          options={streets?.map((item) => {
                                            return {
                                              value: item,
                                              label: item?.value,
                                            };
                                          })}
                                          filterOption={(option, input) => {
                                            return normalize(option.label).includes(normalize(input));
                                          }}
                                          placeholder="Select street"
                                        />
                                        {error.street !== undefined &&
                                          error.street !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.street}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Building
                                        </label>
                                        <input
                                          value={branch?.building ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              building: e.target.value,
                                            });
                                            setError({
                                              ...error,
                                              building: "",
                                            });
                                          }}
                                          autoComplete="off"
                                          id="building_name"
                                          className="form-control"
                                          placeholder="Enter Building Name"
                                        />
                                        {error.building !== undefined &&
                                          error.building !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.building}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Jadda
                                        </label>
                                        <input
                                          value={branch?.jadda ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              jadda: e.target.value,
                                            });
                                            setError({
                                              ...error,
                                              jadda: "",
                                            });
                                          }}
                                          autoComplete="off"
                                          id="jadda_name"
                                          className="form-control"
                                          placeholder="Enter Jadda Name"
                                        />
                                        {error.jadda !== undefined &&
                                          error.jadda !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.jadda}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Apartment
                                        </label>
                                        <input
                                          value={branch?.apartment ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              apartment: e.target.value,
                                            });
                                            setError({
                                              ...error,
                                              apartment: "",
                                            });
                                          }}
                                          autoComplete="off"
                                          id="apartment_name"
                                          className="form-control"
                                          placeholder="Enter Apartment Name"
                                        />
                                        {error.apartment !== undefined &&
                                          error.apartment !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.apartment}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Floor
                                        </label>
                                        <input
                                          value={branch?.floor ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              floor: e.target.value,
                                            });
                                            setError({
                                              ...error,
                                              floor: "",
                                            });
                                          }}
                                          autoComplete="off"
                                          id="floor_name"
                                          className="form-control"
                                          placeholder="Enter Floor"
                                        />
                                        {error.floor !== undefined &&
                                          error.floor !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.floor}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Latitude
                                        </label>
                                        <input
                                          value={branch?.latitude ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              latitude: e.target.value,
                                            });
                                            setError({
                                              ...error,
                                              latitude: "",
                                            });
                                          }}
                                          autoComplete="off"
                                          id="latitude_name"
                                          className="form-control"
                                          placeholder="Enter Latitude"
                                        />
                                        {error.latitude !== undefined &&
                                          error.latitude !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.latitude}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Longitude
                                        </label>
                                        <input
                                          value={branch?.longitude ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              longitude: e.target.value,
                                            });
                                            setError({
                                              ...error,
                                              longitude: "",
                                            });
                                          }}
                                          autoComplete="off"
                                          id="longitude_name"
                                          className="form-control"
                                          placeholder="Enter Longitude"
                                        />
                                        {error.longitude !== undefined &&
                                          error.longitude !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.longitude}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-12 mt-3">
                                        <label className="form-label">
                                          Tutorial Video URL (YouTube link)
                                        </label>
                                        <input
                                          value={branch?.tutorialVideoUrl ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              tutorialVideoUrl: e.target.value,
                                            });
                                          }}
                                          autoComplete="off"
                                          id="tutorial_video_url"
                                          className="form-control"
                                          placeholder="https://www.youtube.com/watch?v=..."
                                        />
                                        <small className="text-muted">
                                          Shown to the driver as a "how to find this branch" guide once they accept an order here.
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-6 precised-address-map">
                                    <PrecisedAddressMap
                                      data={branch}
                                      setData={setBranch}
                                      setErrors={setError}
                                    />
                                  </div>
                                </div>
                                {/*begin::Actions*/}
                                <div className="d-flex flex-row-fluid pt-3">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary me-3"
                                    onClick={() => {
                                      setBranch({});
                                      setError({});
                                      setPopup("");
                                      setBlocks([]);
                                      setStreets([]);
                                    }}
                                  >
                                    Close
                                  </button>

                                  <button
                                    onClick={addBranch}
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    id="kt_modal_upgrade_plan_btn"
                                  >
                                    {/*begin::Indicator label*/}
                                    <span className="indicator-label">
                                      Save
                                    </span>
                                    {/*end::Indicator label*/}
                                    {/*end::Indicator progress*/}
                                  </button>
                                </div>
                                {/*end::Actions*/}
                              </div>
                              {/*end::Modal body*/}
                            </div>
                            {/*end::Modal content*/}
                          </div>
                          {/*end::Modal dialog*/}
                        </div>
                        {/*end::Modal - Add Branches*/}
                        {/*begin::Modal - Edit Branch*/}
                        <div
                          className={
                            popup === "edit-branch"
                              ? "modal fade show d-block"
                              : "modal fade"
                          }
                          tabIndex="-1"
                          id="editBranchesModal"
                          aria-hidden="true"
                        >
                          {/*begin::Modal dialog*/}
                          <div className="modal-dialog modal-dialog-centered modal-xl">
                            {/*begin::Modal content*/}
                            <div className="modal-content rounded">
                              {/*begin::Modal header*/}
                              <div className="modal-header">
                                {/*begin::Close*/}
                                <h4>Edit Branch</h4>
                                {/*end::Close*/}
                              </div>
                              {/*end::Modal header*/}

                              {/*begin::Modal body*/}
                              <div className="modal-body p-4">
                                <div className="row">
                                  <div className="col-md-6">
                                    <div className="row p-5">
                                      <div className="col-md-6">
                                        <label className="form-label">
                                          Name
                                        </label>
                                        <input
                                          value={branch?.name ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              name: e.target.value,
                                            });
                                            setError({ ...error, name: "" });
                                          }}
                                          autoComplete="off"
                                          id="branch_name"
                                          className="form-control"
                                          placeholder="Enter Branch Name"
                                        />
                                        {error.name !== undefined &&
                                          error.name !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.name}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6">
                                        <label className="form-label">
                                          Phone
                                        </label>
                                        <input
                                          value={branch?.phone ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              phone: e.target.value,
                                            });
                                            setError({ ...error, phone: "" });
                                          }}
                                          autoComplete="off"
                                          id="branch_phone"
                                          className="form-control"
                                          placeholder="Enter Branch Phone"
                                        />
                                        {error.phone !== undefined &&
                                          error.phone !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.phone}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Area
                                        </label>
                                        <Select
                                          className="react-select-styled react-select-bordered"
                                          classNamePrefix="react-select"
                                          value={
                                            branch?.area
                                              ? {
                                                label: branch?.area?.value,
                                                value: branch?.area?.value,
                                              }
                                              : null
                                          }
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              area: e?.value,
                                            });
                                            setError({ ...error, area: "" });
                                            setBranch(
                                              ({ block, street, ...rest }) =>
                                                rest,
                                            );
                                            fetchBlockList(e?.value);
                                          }}
                                          options={areas?.map((item) => {
                                            return {
                                              value: item,
                                              label: item?.value,
                                            };
                                          })}
                                          filterOption={(option, input) => {
                                            return normalize(option.label).includes(normalize(input));
                                          }}
                                          placeholder="Select area"
                                        />
                                        {error.area !== undefined &&
                                          error.area !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.area}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Block
                                        </label>
                                        <Select
                                          className="react-select-styled react-select-bordered"
                                          classNamePrefix="react-select"
                                          value={
                                            branch?.block
                                              ? {
                                                label: branch?.block?.value,
                                                value: branch?.block?.value,
                                              }
                                              : null
                                          }
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              block: e?.value,
                                            });
                                            setError({ ...error, block: "" });
                                            setBranch(
                                              ({ street, ...rest }) => rest,
                                            );
                                            fetchStreetList(e?.value);
                                          }}
                                          options={blocks?.map((item) => {
                                            return {
                                              value: item,
                                              label: item?.value,
                                            };
                                          })}
                                          filterOption={(option, input) => {
                                            return normalize(option.label).includes(normalize(input));
                                          }}
                                          placeholder="Select block"
                                        />
                                        {error.block !== undefined &&
                                          error.block !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.block}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-12 mt-3">
                                        <label className="form-label">
                                          Street
                                        </label>
                                        <Select
                                          className="react-select-styled react-select-bordered"
                                          classNamePrefix="react-select"
                                          value={
                                            branch?.street
                                              ? {
                                                label: branch?.street?.value,
                                                value: branch?.street?.value,
                                              }
                                              : null
                                          }
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              street: e?.value,
                                            });
                                            setError({ ...error, street: "" });
                                          }}
                                          options={streets?.map((item) => {
                                            return {
                                              value: item,
                                              label: item?.value,
                                            };
                                          })}
                                          filterOption={(option, input) => {
                                            return normalize(option.label).includes(normalize(input));
                                          }}
                                          placeholder="Select street"
                                        />
                                        {error.street !== undefined &&
                                          error.street !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.street}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Building
                                        </label>
                                        <input
                                          value={branch?.building ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              building: e.target.value,
                                            });
                                            setError({
                                              ...error,
                                              building: "",
                                            });
                                          }}
                                          autoComplete="off"
                                          id="building_name"
                                          className="form-control"
                                          placeholder="Enter Building Name"
                                        />
                                        {error.building !== undefined &&
                                          error.building !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.building}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Jadda
                                        </label>
                                        <input
                                          value={branch?.jadda ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              jadda: e.target.value,
                                            });
                                            setError({
                                              ...error,
                                              jadda: "",
                                            });
                                          }}
                                          autoComplete="off"
                                          id="jadda_name"
                                          className="form-control"
                                          placeholder="Enter Jadda Name"
                                        />
                                        {error.jadda !== undefined &&
                                          error.jadda !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.jadda}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Apartment
                                        </label>
                                        <input
                                          value={branch?.apartment ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              apartment: e.target.value,
                                            });
                                            setError({
                                              ...error,
                                              apartment: "",
                                            });
                                          }}
                                          autoComplete="off"
                                          id="apartment_name"
                                          className="form-control"
                                          placeholder="Enter Apartment Name"
                                        />
                                        {error.apartment !== undefined &&
                                          error.apartment !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.apartment}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Floor
                                        </label>
                                        <input
                                          value={branch?.floor ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              floor: e.target.value,
                                            });
                                            setError({
                                              ...error,
                                              floor: "",
                                            });
                                          }}
                                          autoComplete="off"
                                          id="floor_name"
                                          className="form-control"
                                          placeholder="Enter Floor"
                                        />
                                        {error.floor !== undefined &&
                                          error.floor !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.floor}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Latitude
                                        </label>
                                        <input
                                          value={branch?.latitude ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              latitude: e.target.value,
                                            });
                                            setError({
                                              ...error,
                                              latitude: "",
                                            });
                                          }}
                                          autoComplete="off"
                                          id="latitude_name"
                                          className="form-control"
                                          placeholder="Enter Latitude"
                                        />
                                        {error.latitude !== undefined &&
                                          error.latitude !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.latitude}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-6 mt-3">
                                        <label className="form-label">
                                          Longitude
                                        </label>
                                        <input
                                          value={branch?.longitude ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              longitude: e.target.value,
                                            });
                                            setError({
                                              ...error,
                                              longitude: "",
                                            });
                                          }}
                                          autoComplete="off"
                                          id="longitude_name"
                                          className="form-control"
                                          placeholder="Enter Longitude"
                                        />
                                        {error.longitude !== undefined &&
                                          error.longitude !== "" ? (
                                          <small className="text-danger mt-2">
                                            {error.longitude}
                                          </small>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="col-md-12 mt-3">
                                        <label className="form-label">
                                          Tutorial Video URL (YouTube link)
                                        </label>
                                        <input
                                          value={branch?.tutorialVideoUrl ?? ""}
                                          onChange={(e) => {
                                            setBranch({
                                              ...branch,
                                              tutorialVideoUrl: e.target.value,
                                            });
                                          }}
                                          autoComplete="off"
                                          id="tutorial_video_url"
                                          className="form-control"
                                          placeholder="https://www.youtube.com/watch?v=..."
                                        />
                                        <small className="text-muted">
                                          Shown to the driver as a "how to find this branch" guide once they accept an order here.
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-6 precised-address-map">
                                    <PrecisedAddressMap
                                      data={branch}
                                      setData={setBranch}
                                      setErrors={setError}
                                    />
                                  </div>
                                </div>

                                {/*begin::Actions*/}
                                <div className="d-flex flex-row-fluid pt-3">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary me-3"
                                    onClick={() => {
                                      setBranch({});
                                      setError({});
                                      setPopup("");
                                      setBlocks([]);
                                      setStreets([]);
                                    }}
                                  >
                                    Close
                                  </button>

                                  <button
                                    onClick={editBranch}
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    id="kt_modal_upgrade_plan_btn"
                                  >
                                    {/*begin::Indicator label*/}
                                    <span className="indicator-label">
                                      Update
                                    </span>
                                    {/*end::Indicator label*/}
                                    {/*end::Indicator progress*/}
                                  </button>
                                </div>
                                {/*end::Actions*/}
                              </div>
                              {/*end::Modal body*/}
                            </div>
                            {/*end::Modal content*/}
                          </div>
                          {/*end::Modal dialog*/}
                        </div>
                        {/*end::Modal - Edit Branch*/}

                        {/*begin::Modal - Delete Branches*/}
                        <div
                          className={
                            popup === "delete-branch"
                              ? "modal fade show d-block"
                              : "modal fade"
                          }
                          tabIndex="-1"
                          id="deleteBranchesModal"
                          aria-hidden="true"
                        >
                          {/*begin::Modal dialog*/}
                          <div className="modal-dialog modal-dialog-centered modal-sm">
                            {/*begin::Modal content*/}
                            <div className="modal-content text-center bg-danger text-light">
                              {/*begin::Modal header*/}
                              <div className="modal-header">
                                {/*begin::Close*/}
                                <h5 className="modal-title mx-auto text-light">
                                  DELETE BRANCH
                                </h5>
                                {/*end::Close*/}
                              </div>
                              {/*end::Modal header*/}

                              {/*begin::Modal body*/}
                              <div className="modal-body p-4">
                                <h4 className="text-center my-5 text-light">
                                  Are You Sure ?
                                </h4>
                              </div>
                              {/*end::Modal body*/}
                              {/*begin::Actions*/}
                              <div className="modal-footer justify-content-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-light me-3"
                                  onClick={() => {
                                    setBranch({});
                                    setError({});
                                    setPopup("");
                                  }}
                                >
                                  Close
                                </button>

                                <button
                                  onClick={deleteBranch}
                                  type="button"
                                  className="btn btn-light btn-sm"
                                  id="kt_modal_upgrade_plan_btn"
                                >
                                  {/*begin::Indicator label*/}
                                  <span className="indicator-label">
                                    Delete
                                  </span>
                                  {/*end::Indicator label*/}
                                  {/*end::Indicator progress*/}
                                </button>
                              </div>
                              {/*end::Actions*/}
                            </div>
                            {/*end::Modal content*/}
                          </div>
                          {/*end::Modal dialog*/}
                        </div>
                        {/*end::Modal - Delete Branches*/}
                      </div>
                      {/*end::Col*/}

                      {/*begin::Col*/}
                      <div className="col ps-0 mb-xl-10" id="loadContacts">
                        <h1 className="d-flex text-dark fw-bold m-0 fs-3 mb-5">
                          Contacts
                        </h1>
                        {/*begin::Tables Widget 3*/}
                        <PermissionGate required={`view_merchant_contact`}>
                          <div className="card">
                            {/*begin::Body*/}
                            <div className="card-body">
                              {contacts && contacts.length > 0 ? (
                                <div className="table-responsive">
                                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                                    <thead>
                                      <tr className="text-muted fw-bold fs-7 text-uppercase gs-0">
                                        <th className="text-center min-w-100px">
                                          Name
                                        </th>
                                        <th className="text-center min-w-100px">
                                          Email
                                        </th>
                                        <th className="text-center min-w-100px">
                                          Phone
                                        </th>
                                        <th className="text-center min-w-100px">
                                          Actions
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody
                                      className="text-gray-600 fw-semibold"
                                      id="filterDataWrap"
                                    >
                                      {contacts &&
                                        contacts.map((contact, index) => {
                                          return (
                                            <tr
                                              key={index}
                                              className="text-center"
                                            >
                                              <td>{contact?.name}</td>
                                              <td>{contact?.email}</td>
                                              <td>{contact?.phone}</td>
                                              <td className="">
                                                <div className="btn-group">
                                                  {/*begin::Edit*/}
                                                  <PermissionGate
                                                    required={`edit_merchant_contact`}
                                                    fallback={<></>}
                                                  >
                                                    <button
                                                      onClick={() => {
                                                        openPopup(
                                                          "edit-contact",
                                                          contact,
                                                        );
                                                      }}
                                                      className="btn px-2 btn-outline btn-primary btn-color-white btn-sm"
                                                      title="Edit Contact"
                                                    >
                                                      <i className="bi-duotone bi-pen fs-5"></i>
                                                    </button>
                                                  </PermissionGate>

                                                  {/*end::Edit*/}
                                                  {/*begin::Delete*/}
                                                  <PermissionGate
                                                    required={`delete_merchant_contact`}
                                                    fallback={<></>}
                                                  >
                                                    <button
                                                      onClick={() => {
                                                        openPopup(
                                                          "delete-contact",
                                                          contact,
                                                        );
                                                      }}
                                                      className="btn px-2 btn-outline btn-danger btn-color-white btn-sm"
                                                      title="Delete Contact"
                                                    >
                                                      <i className="bi-duotone bi-trash fs-5"></i>
                                                    </button>
                                                  </PermissionGate>

                                                  {/*end::Delete*/}
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-center w-100">
                                  <img
                                    className="w-25 mx-auto py-20"
                                    src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/misc/no-content.png`}
                                    alt="no-content"
                                  />
                                </div>
                              )}
                              <PermissionGate
                                required={`add_merchant_contact`}
                                fallback={<></>}
                              >
                                <button
                                  onClick={() => {
                                    openPopup("add-contact", {});
                                  }}
                                  className="btn btn-success btn-sm w-100 me-3"
                                >
                                  Add
                                </button>
                              </PermissionGate>
                            </div>
                            {/*begin::Body*/}
                          </div>
                        </PermissionGate>

                        {/*end::Tables Widget 3*/}
                        {/*begin::Modal - Add Contact*/}
                        <div
                          className={
                            popup && popup === "add-contact"
                              ? "modal fade show d-block"
                              : "modal fade"
                          }
                          id="addContactModal"
                          tabIndex="-1"
                          aria-hidden="true"
                        >
                          {/*begin::Modal dialog*/}
                          <div className="modal-dialog modal-dialog-centered modal-lg">
                            {/*begin::Modal content*/}
                            <div className="modal-content rounded">
                              {/*begin::Modal header*/}
                              <div className="modal-header">
                                {/*begin::Close*/}
                                <h4>Add Contact</h4>
                                {/*end::Close*/}
                              </div>
                              {/*end::Modal header*/}

                              {/*begin::Modal body*/}
                              <div className="modal-body p-4">
                                <div className="row">
                                  <div className="col">
                                    <label className="form-label">Title</label>
                                    <input
                                      value={contact?.title ?? ""}
                                      onChange={(e) => {
                                        setContact({
                                          ...contact,
                                          title: e.target.value,
                                        });
                                        setError({ ...error, title: "" });
                                      }}
                                      id="contact_title"
                                      className="form-control"
                                      placeholder="e.g. Manager"
                                      autoComplete="off"
                                    />
                                    {error.title !== undefined &&
                                      error.title !== "" ? (
                                      <small className="text-danger mt-2">
                                        {error.title}
                                      </small>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                  <div className="col">
                                    <label className="form-label">Name</label>
                                    <input
                                      value={contact?.name ?? ""}
                                      onChange={(e) => {
                                        setContact({
                                          ...contact,
                                          name: e.target.value,
                                        });
                                        setError({ ...error, name: "" });
                                      }}
                                      id="contact_name"
                                      className="form-control"
                                      placeholder="Enter Name"
                                      autoComplete="off"
                                    />
                                    {error.name !== undefined &&
                                      error.name !== "" ? (
                                      <small className="text-danger mt-2">
                                        {error.name}
                                      </small>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col">
                                    <label className="form-label">Email</label>
                                    <input
                                      value={contact?.email ?? ""}
                                      onChange={(e) => {
                                        setContact({
                                          ...contact,
                                          email: e.target.value,
                                        });
                                        setError({ ...error, email: "" });
                                      }}
                                      id="contact_email"
                                      className="form-control"
                                      placeholder="Enter Email"
                                      autoComplete="off"
                                    />
                                    {error.email !== undefined &&
                                      error.email !== "" ? (
                                      <small className="text-danger mt-2">
                                        {error.email}
                                      </small>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                  <div className="col">
                                    <label className="form-label">Phone</label>
                                    <input
                                      value={contact?.phone ?? ""}
                                      onChange={(e) => {
                                        setContact({
                                          ...contact,
                                          phone: e.target.value,
                                        });
                                        setError({ ...error, phone: "" });
                                      }}
                                      id="contact_phone"
                                      className="form-control"
                                      placeholder="Enter Phone"
                                      autoComplete="off"
                                    />
                                    {error.phone !== undefined &&
                                      error.phone !== "" ? (
                                      <small className="text-danger mt-2">
                                        {error.phone}
                                      </small>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                  <div className="col">
                                    <label className="form-label">
                                      Office Phone
                                    </label>
                                    <input
                                      value={contact?.officePhone ?? ""}
                                      onChange={(e) => {
                                        setContact({
                                          ...contact,
                                          officePhone: e.target.value,
                                        });
                                        setError({ ...error, officePhone: "" });
                                      }}
                                      id="contact_office_phone"
                                      className="form-control"
                                      placeholder="Enter Office Phone"
                                      autoComplete="off"
                                    />
                                    {error.officePhone !== undefined &&
                                      error.officePhone !== "" ? (
                                      <small className="text-danger mt-2">
                                        {error.officePhone}
                                      </small>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                </div>

                                {/*begin::Actions*/}
                                <div className="d-flex flex-row-fluid pt-12">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary me-3"
                                    onClick={() => {
                                      setPopup("");
                                    }}
                                  >
                                    Close
                                  </button>

                                  <button
                                    onClick={addContact}
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                  >
                                    Save
                                  </button>
                                </div>
                                {/*end::Actions*/}
                              </div>
                              {/*end::Modal body*/}
                            </div>
                            {/*end::Modal content*/}
                          </div>
                          {/*end::Modal dialog*/}
                        </div>
                        {/*end::Modal - Add Contact*/}
                        {/*begin::Modal - Edit Contact*/}
                        <div
                          className={
                            popup && popup === "edit-contact"
                              ? "modal fade show d-block"
                              : "modal fade"
                          }
                          id="editContactModal"
                          tabIndex="-1"
                          aria-hidden="true"
                        >
                          {/*begin::Modal dialog*/}
                          <div className="modal-dialog modal-dialog-centered modal-lg">
                            {/*begin::Modal content*/}
                            <div className="modal-content rounded">
                              {/*begin::Modal header*/}
                              <div className="modal-header">
                                {/*begin::Close*/}
                                <h4>Edit Contact</h4>
                                {/*end::Close*/}
                              </div>
                              {/*end::Modal header*/}

                              {/*begin::Modal body*/}
                              <div className="modal-body p-4">
                                <div className="row">
                                  <div className="col">
                                    <label className="form-label">Title</label>
                                    <input
                                      value={contact?.title ?? ""}
                                      onChange={(e) => {
                                        setContact({
                                          ...contact,
                                          title: e.target.value,
                                        });
                                        setError({ ...error, title: "" });
                                      }}
                                      id="contact_title"
                                      className="form-control"
                                      placeholder="e.g. Manager"
                                      autoComplete="off"
                                    />
                                    {error.title !== undefined &&
                                      error.title !== "" ? (
                                      <small className="text-danger mt-2">
                                        {error.title}
                                      </small>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                  <div className="col">
                                    <label className="form-label">Name</label>
                                    <input
                                      value={contact?.name ?? ""}
                                      onChange={(e) => {
                                        setContact({
                                          ...contact,
                                          name: e.target.value,
                                        });
                                        setError({ ...error, name: "" });
                                      }}
                                      id="contact_name"
                                      className="form-control"
                                      placeholder="Enter Name"
                                      autoComplete="off"
                                    />
                                    {error.name !== undefined &&
                                      error.name !== "" ? (
                                      <small className="text-danger mt-2">
                                        {error.name}
                                      </small>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col">
                                    <label className="form-label">Email</label>
                                    <input
                                      value={contact?.email ?? ""}
                                      onChange={(e) => {
                                        setContact({
                                          ...contact,
                                          email: e.target.value,
                                        });
                                        setError({ ...error, email: "" });
                                      }}
                                      id="contact_email"
                                      className="form-control"
                                      placeholder="Enter Email"
                                      autoComplete="off"
                                    />
                                    {error.email !== undefined &&
                                      error.email !== "" ? (
                                      <small className="text-danger mt-2">
                                        {error.email}
                                      </small>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                  <div className="col">
                                    <label className="form-label">Phone</label>
                                    <input
                                      value={contact?.phone ?? ""}
                                      onChange={(e) => {
                                        setContact({
                                          ...contact,
                                          phone: e.target.value,
                                        });
                                        setError({ ...error, phone: "" });
                                      }}
                                      id="contact_phone"
                                      className="form-control"
                                      placeholder="Enter Phone"
                                      autoComplete="off"
                                    />
                                    {error.phone !== undefined &&
                                      error.phone !== "" ? (
                                      <small className="text-danger mt-2">
                                        {error.phone}
                                      </small>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                  <div className="col">
                                    <label className="form-label">
                                      Office Phone
                                    </label>
                                    <input
                                      value={contact?.officePhone ?? ""}
                                      onChange={(e) => {
                                        setContact({
                                          ...contact,
                                          officePhone: e.target.value,
                                        });
                                        setError({ ...error, officePhone: "" });
                                      }}
                                      id="contact_office_phone"
                                      className="form-control"
                                      placeholder="Enter Office Phone"
                                      autoComplete="off"
                                    />
                                    {error.officePhone !== undefined &&
                                      error.officePhone !== "" ? (
                                      <small className="text-danger mt-2">
                                        {error.officePhone}
                                      </small>
                                    ) : (
                                      ""
                                    )}
                                  </div>
                                </div>

                                {/*begin::Actions*/}
                                <div className="d-flex flex-row-fluid pt-12">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary me-3"
                                    onClick={() => {
                                      setPopup("");
                                    }}
                                  >
                                    Close
                                  </button>

                                  <button
                                    onClick={editContact}
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                  >
                                    Update
                                  </button>
                                </div>
                                {/*end::Actions*/}
                              </div>
                              {/*end::Modal body*/}
                            </div>
                            {/*end::Modal content*/}
                          </div>
                          {/*end::Modal dialog*/}
                        </div>
                        {/*end::Modal - Edit Contact*/}
                        {/*begin::Modal - Delete Contact*/}
                        <div
                          className={
                            popup === "delete-contact"
                              ? "modal fade show d-block"
                              : "modal fade"
                          }
                          tabIndex="-1"
                          id="deleteContactModal"
                          aria-hidden="true"
                        >
                          {/*begin::Modal dialog*/}
                          <div className="modal-dialog modal-dialog-centered modal-sm">
                            {/*begin::Modal content*/}
                            <div className="modal-content text-center bg-danger text-light">
                              {/*begin::Modal header*/}
                              <div className="modal-header">
                                {/*begin::Close*/}
                                <h5 className="modal-title mx-auto text-light">
                                  DELETE CONTACT
                                </h5>
                                {/*end::Close*/}
                              </div>
                              {/*end::Modal header*/}

                              {/*begin::Modal body*/}
                              <div className="modal-body p-4">
                                <h4 className="text-center my-5 text-light">
                                  Are You Sure ?
                                </h4>
                              </div>
                              {/*end::Modal body*/}
                              {/*begin::Actions*/}
                              <div className="modal-footer justify-content-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-light me-3"
                                  onClick={() => {
                                    setContact({});
                                    setError({});
                                    setPopup("");
                                  }}
                                >
                                  Close
                                </button>

                                <button
                                  onClick={deleteContact}
                                  type="button"
                                  className="btn btn-light btn-sm"
                                  id="kt_modal_upgrade_plan_btn"
                                >
                                  {/*begin::Indicator label*/}
                                  <span className="indicator-label">
                                    Delete
                                  </span>
                                  {/*end::Indicator label*/}
                                  {/*end::Indicator progress*/}
                                </button>
                              </div>
                              {/*end::Actions*/}
                            </div>
                            {/*end::Modal content*/}
                          </div>
                          {/*end::Modal dialog*/}
                        </div>
                        {/*end::Modal - Delete Contact*/}
                      </div>
                      {/*end::Col*/}
                    </div>
                    {/*end::Row*/}

                    {/*begin::Row*/}
                    <div className="row g-5 g-lg-10 mt-4">
                      {/*begin::Col*/}
                      <div className="col ps-0 mb-xl-10" id="loadEmployees">
                        <h1 className="d-flex text-dark fw-bold m-0 fs-3 mb-5">
                          Employees
                        </h1>
                        {/*begin::Tables Widget 3*/}
                        <PermissionGate required={`view_merchant_employees`}>
                          <div className="card">
                            {/*begin::Body*/}
                            <div className="card-body">
                              {/* @if ($employees->count() > 0) */}
                              {employees && employees.length > 0 ? (
                                <div className="table-responsive">
                                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                                    <thead>
                                      <tr className="text-muted fw-bold fs-7 text-uppercase gs-0">
                                        <th className="text-center min-w-100px">
                                          Name
                                        </th>
                                        <th className="text-center min-w-100px">
                                          Email
                                        </th>
                                        <th className="text-center min-w-100px">
                                          Role
                                        </th>
                                        <th className="text-center min-w-100px">
                                          Created At
                                        </th>
                                        <th className="text-center min-w-100px">
                                          Branch
                                        </th>
                                        <th className="text-center min-w-100px">
                                          Actions
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="text-gray-600 fw-semibold">
                                      {employees &&
                                        employees.map((employee, index) => {
                                          return (
                                            <tr
                                              key={index}
                                              className="text-center"
                                            >
                                              <td>{employee?.name}</td>
                                              <td>{employee?.email}</td>
                                              <td>{employee?.role}</td>
                                              <td>
                                                {moment(employee?.createdAt)
                                                  .tz(
                                                    process.env
                                                      .REACT_APP_TIMEZONE,
                                                  )
                                                  .format("Do MMM, YYYY")}
                                              </td>
                                              <td>{employee?.branchName}</td>
                                              <td className="">
                                                <div className="btn-group">
                                                  {/*begin::Edit*/}
                                                  <PermissionGate
                                                    required={`edit_merchant_employees`}
                                                    fallback={<></>}
                                                  >
                                                    <button
                                                      onClick={() => {
                                                        openPopup(
                                                          "edit-employee",
                                                          employee,
                                                        );
                                                      }}
                                                      className="btn px-2 btn-outline btn-primary btn-color-white btn-sm"
                                                      title="Edit Employee"
                                                    >
                                                      <i className="bi-duotone bi-pen fs-5"></i>
                                                    </button>
                                                  </PermissionGate>
                                                  {/*end::Edit*/}

                                                  {/*begin::Reset Password*/}
                                                  <PermissionGate
                                                    required={`reset_password_merchant_employees`}
                                                    fallback={<></>}
                                                  >
                                                    <button
                                                      onClick={() => {
                                                        openPopup(
                                                          "reset-password-employee",
                                                          employee,
                                                        );
                                                      }}
                                                      className="btn px-2 btn-outline btn-warning btn-color-white btn-sm"
                                                      data-bs-toggle="tooltip"
                                                      data-bs-placement="top"
                                                      title="Reset Password"
                                                    >
                                                      <i className="bi-duotone bi-key fs-5"></i>
                                                    </button>
                                                  </PermissionGate>
                                                  {/*end::Reset Password*/}

                                                  {/*begin::Change Password*/}
                                                  <PermissionGate
                                                    required={`change_password_merchant_employees`}
                                                    fallback={<></>}
                                                  >
                                                    <button
                                                      onClick={() => {
                                                        openPopup(
                                                          "change-employee-password",
                                                          employee,
                                                        );
                                                      }}
                                                      className="btn px-2 btn-outline btn-warning btn-color-white btn-sm"
                                                      data-bs-toggle="tooltip"
                                                      data-bs-placement="top"
                                                      title="Change Password"
                                                    >
                                                      <i className="bi-duotone bi-shield-lock-fill fs-5"></i>
                                                    </button>
                                                  </PermissionGate>
                                                  {/*end::Change Password*/}

                                                  {/*begin::Delete*/}
                                                  <PermissionGate
                                                    required={`delete_merchant_employees`}
                                                    fallback={<></>}
                                                  >
                                                    <button
                                                      onClick={() => {
                                                        openPopup(
                                                          "delete-employee",
                                                          employee,
                                                        );
                                                      }}
                                                      className="btn px-2 btn-outline btn-danger btn-color-white btn-sm"
                                                      title="Delete Employee"
                                                    >
                                                      <i className="bi-duotone bi-trash fs-5"></i>
                                                    </button>
                                                  </PermissionGate>
                                                  {/*end::Delete*/}
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-center w-100">
                                  <img
                                    className="w-25 mx-auto py-20"
                                    src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/misc/no-content.png`}
                                    alt="no-content"
                                  />
                                </div>
                              )}
                              <PermissionGate
                                required={`add_merchant_employees`}
                                fallback={<></>}
                              >
                                <button
                                  onClick={() => {
                                    openPopup("add-employee", {});
                                  }}
                                  className="btn btn-success btn-sm w-100 me-3"
                                >
                                  Add
                                </button>
                              </PermissionGate>
                            </div>
                            {/*begin::Body*/}
                          </div>
                        </PermissionGate>
                        {/*end::Tables Widget 3*/}
                        {/*begin::Modal - Add Employee */}
                        {popup && popup === "add-employee" ? (
                          <div
                            className="modal fade show d-block"
                            id="addEmployeeModal"
                            tabIndex="-1"
                            aria-hidden="true"
                          >
                            {/*begin::Modal dialog*/}
                            <div className="modal-dialog modal-dialog-centered modal-lg">
                              {/*begin::Modal content*/}
                              <div className="modal-content rounded">
                                {/*begin::Modal header*/}
                                <div className="modal-header">
                                  {/*begin::Close*/}
                                  <h4>Add Employee</h4>
                                  {/*end::Close*/}
                                </div>
                                {/*end::Modal header*/}

                                {/*begin::Modal body*/}
                                <div className="modal-body p-4">
                                  <div className="row">
                                    <div className="col">
                                      <label className="form-label required">
                                        Name
                                      </label>
                                      <input
                                        value={employee?.name ?? ""}
                                        onChange={(e) => {
                                          setEmployee({
                                            ...employee,
                                            name: e.target.value,
                                          });
                                          setError({ ...error, name: "" });
                                        }}
                                        id="employee_name"
                                        className="form-control"
                                        placeholder="Employee Name"
                                        autoComplete="off"
                                      />
                                      {error.name !== undefined &&
                                        error.name !== "" ? (
                                        <small className="text-danger mt-2">
                                          {error.name}
                                        </small>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                    <div className="col">
                                      <label className="form-label required">
                                        Email
                                      </label>
                                      <input
                                        value={employee?.email ?? ""}
                                        onChange={(e) => {
                                          setEmployee({
                                            ...employee,
                                            email: e.target.value,
                                          });
                                          setError({ ...error, email: "" });
                                        }}
                                        id="employee_email"
                                        className="form-control"
                                        placeholder="Employee Email"
                                        autoComplete="off"
                                      />
                                      {error.email !== undefined &&
                                        error.email !== "" ? (
                                        <small className="text-danger mt-2">
                                          {error.email}
                                        </small>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                  </div>
                                  <div className="row mt-3">
                                    <div className="col">
                                      <label className="form-label required">
                                        Role
                                      </label>
                                      {/*begin::Input group*/}
                                      <div className="row mb-0 text-start">
                                        {/*begin::Input group*/}
                                        <div className="mb-5">
                                          <Select
                                            className="react-select-styled react-select-bordered"
                                            classNamePrefix="react-select"
                                            defaultInputValue={
                                              employee?.role ?? ""
                                            }
                                            onChange={(e) => {
                                              setEmployee({
                                                ...employee,
                                                role: e.value,
                                              });
                                              setError({ ...error, role: "" });
                                            }}
                                            options={[
                                              {
                                                value: "Employee",
                                                label: "Employee",
                                              },
                                              {
                                                value: "Owner",
                                                label: "Owner",
                                              },
                                              {
                                                value: "Manager",
                                                label: "Manager",
                                              },
                                              {
                                                value: "Accountant",
                                                label: "Accountant",
                                              },
                                            ]}
                                            filterOption={(option, input) => {
                                              return normalize(option.label).includes(normalize(input));
                                            }}
                                            placeholder="Select an option"
                                          />
                                        </div>
                                        {/*end::Input group*/}
                                      </div>
                                      {/*end::Input group*/}
                                      {error.role !== undefined &&
                                        error.role !== "" ? (
                                        <small className="text-danger mt-2">
                                          {error.role}
                                        </small>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                    <div className="col">
                                      <label className="form-label required">
                                        Branch
                                      </label>
                                      {/*begin::Input group*/}
                                      <div className="row mb-0 text-start">
                                        {/*begin::Input group*/}
                                        <div className="mb-5">
                                          <Select
                                            className="react-select-styled react-select-bordered"
                                            classNamePrefix="react-select"
                                            defaultInputValue={
                                              employee?.branchId ?? ""
                                            }
                                            onChange={(e) => {
                                              setEmployee({
                                                ...employee,
                                                branchId: e.value,
                                              });
                                              setError({
                                                ...error,
                                                branchId: "",
                                              });
                                            }}
                                            options={branches?.map((item) => {
                                              return {
                                                value: item._id,
                                                label: item.name,
                                              };
                                            })}
                                            filterOption={(option, input) => {
                                              return normalize(option.label).includes(normalize(input));
                                            }}
                                            placeholder="Select an option"
                                          />
                                        </div>
                                        {/*end::Input group*/}
                                      </div>
                                      {/*end::Input group*/}
                                      {error.branchId !== undefined &&
                                        error.branchId !== "" ? (
                                        <small className="text-danger mt-2">
                                          {error.branchId}
                                        </small>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                  </div>

                                  {/*begin::Actions*/}
                                  <div className="d-flex flex-row-fluid pt-12">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-secondary me-3"
                                      onClick={() => {
                                        setPopup("");
                                      }}
                                    >
                                      Close
                                    </button>

                                    <button
                                      onClick={addEmployee}
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                    >
                                      Save
                                    </button>
                                  </div>
                                  {/*end::Actions*/}
                                </div>
                                {/*end::Modal body*/}
                              </div>
                              {/*end::Modal content*/}
                            </div>
                            {/*end::Modal dialog*/}
                          </div>
                        ) : (
                          ""
                        )}
                        {/*end::Modal - Add Employee*/}
                        {/*begin::Modal - Edit Employee*/}
                        {popup && popup === "edit-employee" ? (
                          <div
                            className="modal fade show d-block"
                            id="editEmployeeModal"
                            tabIndex="-1"
                            aria-hidden="true"
                          >
                            {/*begin::Modal dialog*/}
                            <div className="modal-dialog modal-dialog-centered modal-lg">
                              {/*begin::Modal content*/}
                              <div className="modal-content rounded">
                                {/*begin::Modal header*/}
                                <div className="modal-header">
                                  {/*begin::Close*/}
                                  <h4>Edit Employee</h4>
                                  {/*end::Close*/}
                                </div>
                                {/*end::Modal header*/}

                                {/*begin::Modal body*/}
                                <div className="modal-body p-4">
                                  <div className="row">
                                    <div className="col">
                                      <label className="form-label required">
                                        Name
                                      </label>
                                      <input
                                        value={employee?.name ?? ""}
                                        onChange={(e) => {
                                          setEmployee({
                                            ...employee,
                                            name: e.target.value,
                                          });
                                          setError({ ...error, name: "" });
                                        }}
                                        id="employee_name"
                                        className="form-control"
                                        placeholder="Employee Name"
                                        autoComplete="off"
                                      />
                                      {error.name !== undefined &&
                                        error.name !== "" ? (
                                        <small className="text-danger mt-2">
                                          {error.name}
                                        </small>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                    <div className="col">
                                      <label className="form-label required">
                                        Email
                                      </label>
                                      <input
                                        value={employee?.email ?? ""}
                                        onChange={(e) => {
                                          setEmployee({
                                            ...employee,
                                            email: e.target.value,
                                          });
                                          setError({ ...error, email: "" });
                                        }}
                                        id="employee_email"
                                        className="form-control"
                                        placeholder="Employee Email"
                                        autoComplete="off"
                                      />
                                      {error.email !== undefined &&
                                        error.email !== "" ? (
                                        <small className="text-danger mt-2">
                                          {error.email}
                                        </small>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                  </div>
                                  <div className="row mt-3">
                                    <div className="col">
                                      <label className="form-label required">
                                        Role
                                      </label>
                                      {/*begin::Input group*/}
                                      <div className="row mb-0 text-start">
                                        {/*begin::Input group*/}
                                        <div className="mb-5">
                                          <Select
                                            className="react-select-styled react-select-bordered"
                                            classNamePrefix="react-select"
                                            defaultValue={
                                              employee?.role
                                                ? {
                                                  value: employee.role,
                                                  label: employee.role,
                                                }
                                                : null
                                            }
                                            onChange={(e) => {
                                              setEmployee({
                                                ...employee,
                                                role: e.value,
                                              });
                                              setError({ ...error, role: "" });
                                            }}
                                            options={[
                                              {
                                                value: "Employee",
                                                label: "Employee",
                                              },
                                              {
                                                value: "Owner",
                                                label: "Owner",
                                              },
                                              {
                                                value: "Manager",
                                                label: "Manager",
                                              },
                                              {
                                                value: "Accountant",
                                                label: "Accountant",
                                              },
                                            ]}
                                            filterOption={(option, input) => {
                                              return normalize(option.label).includes(normalize(input));
                                            }}
                                            placeholder="Select an option"
                                          />
                                        </div>
                                        {/*end::Input group*/}
                                      </div>
                                      {/*end::Input group*/}
                                      {error.role !== undefined &&
                                        error.role !== "" ? (
                                        <small className="text-danger mt-2">
                                          {error.role}
                                        </small>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                    <div className="col">
                                      <label className="form-label required">
                                        Branch
                                      </label>
                                      {/*begin::Input group*/}
                                      <div className="row mb-0 text-start">
                                        {/*begin::Input group*/}
                                        <div className="mb-5">
                                          <Select
                                            className="react-select-styled react-select-bordered"
                                            classNamePrefix="react-select"
                                            onChange={(e) => {
                                              setEmployee({
                                                ...employee,
                                                branchId: e.value,
                                              });
                                              setError({
                                                ...error,
                                                branchId: "",
                                              });
                                            }}
                                            options={branches?.map((item) => {
                                              return {
                                                value: item._id,
                                                label: item.name,
                                              };
                                            })}
                                            filterOption={(option, input) => {
                                              return normalize(option.label).includes(normalize(input));
                                            }}
                                            defaultValue={
                                              employee?.branchId
                                                ? {
                                                  value: employee.branchId,
                                                  label: employee.branchName,
                                                }
                                                : null
                                            }
                                            placeholder="Select an option"
                                          />
                                        </div>
                                        {/*end::Input group*/}
                                      </div>
                                      {/*end::Input group*/}
                                      {error.branchId !== undefined &&
                                        error.branchId !== "" ? (
                                        <small className="text-danger mt-2">
                                          {error.branchId}
                                        </small>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                  </div>

                                  {/*begin::Actions*/}
                                  <div className="d-flex flex-row-fluid pt-12">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-secondary me-3"
                                      onClick={() => {
                                        setPopup("");
                                      }}
                                    >
                                      Close
                                    </button>

                                    <button
                                      onClick={editEmployee}
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                    >
                                      Update
                                    </button>
                                  </div>
                                  {/*end::Actions*/}
                                </div>
                                {/*end::Modal body*/}
                              </div>
                              {/*end::Modal content*/}
                            </div>
                            {/*end::Modal dialog*/}
                          </div>
                        ) : (
                          ""
                        )}
                        {/*end::Modal - Edit Employee*/}
                        {/*begin::Modal - Delete Employee*/}
                        {popup && popup === "delete-employee" ? (
                          <div
                            className="modal fade show d-block"
                            id="deleteEmployeeModal"
                            tabIndex="-1"
                            aria-hidden="true"
                          >
                            {/*begin::Modal dialog*/}
                            <div className="modal-dialog modal-dialog-centered modal-sm">
                              {/*begin::Modal content*/}
                              <div className="modal-content text-center bg-danger text-light">
                                {/*begin::Modal header*/}
                                <div className="modal-header">
                                  {/*begin::Close*/}
                                  <h5 className="modal-title mx-auto text-light">
                                    DELETE EMPLOYEE
                                  </h5>
                                  {/*end::Close*/}
                                </div>
                                {/*end::Modal header*/}

                                {/*begin::Modal body*/}
                                <div className="modal-body p-4">
                                  <h4 className="text-center my-5 text-light">
                                    Are You Sure ?
                                  </h4>
                                </div>
                                {/*end::Modal body*/}
                                {/*begin::Actions*/}
                                <div className="modal-footer justify-content-center">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary me-3"
                                    onClick={() => {
                                      setEmployee({});
                                      setError({});
                                      setPopup("");
                                    }}
                                  >
                                    Close
                                  </button>

                                  <button
                                    onClick={deleteEmployee}
                                    type="button"
                                    className="btn btn-light btn-sm"
                                    id="kt_modal_upgrade_plan_btn"
                                  >
                                    {/*begin::Indicator label*/}
                                    <span className="indicator-label">
                                      Delete
                                    </span>
                                    {/*end::Indicator label*/}
                                    {/*end::Indicator progress*/}
                                  </button>
                                </div>
                                {/*end::Actions*/}
                              </div>
                              {/*end::Modal content*/}
                            </div>
                            {/*end::Modal dialog*/}
                          </div>
                        ) : (
                          ""
                        )}
                        {/*end::Modal - Delete Employee*/}
                        {/*begin::Modal - Reset Employee Password */}
                        {popup && popup === "reset-password-employee" ? (
                          <div
                            className="modal fade show d-block"
                            id="resetEmployeePasswordModal"
                            tabIndex="-1"
                            aria-hidden="true"
                          >
                            {/*begin::Modal dialog*/}
                            <div className="modal-dialog modal-dialog-centered modal-sm">
                              {/*begin::Modal content*/}
                              <div className="modal-content text-center bg-warning text-light">
                                {/*begin::Modal header*/}
                                <div className="modal-header">
                                  {/*begin::Close*/}
                                  <h5 className="modal-title mx-auto text-light">
                                    RESET PASSWORD
                                  </h5>
                                  {/*end::Close*/}
                                </div>
                                {/*end::Modal header*/}

                                {/*begin::Modal body*/}
                                <div className="modal-body p-4">
                                  <h4 className="text-center my-5 text-light">
                                    Are You Sure ?
                                  </h4>
                                </div>
                                {/*end::Modal body*/}
                                {/*begin::Actions*/}
                                <div className="modal-footer justify-content-center">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary me-3"
                                    onClick={() => {
                                      setEmployee({});
                                      setError({});
                                      setPopup("");
                                    }}
                                  >
                                    Close
                                  </button>

                                  <button
                                    onClick={resetPasswordEmployee}
                                    type="button"
                                    className="btn btn-light btn-sm"
                                    id="kt_modal_upgrade_plan_btn"
                                  >
                                    {/*begin::Indicator label*/}
                                    <span className="indicator-label">
                                      Reset Password
                                    </span>
                                    {/*end::Indicator label*/}
                                    {/*end::Indicator progress*/}
                                  </button>
                                </div>
                                {/*end::Actions*/}
                              </div>
                              {/*end::Modal content*/}
                            </div>
                            {/*end::Modal dialog*/}
                          </div>
                        ) : (
                          ""
                        )}
                        {/*end::Modal - Reset Employee Password*/}
                        {/*begin::Modal - Change Employee Password*/}
                        {popup && popup === "change-employee-password" ? (
                          <div
                            className="modal fade show d-block"
                            id="changeEmployeePasswordModal"
                            tabIndex="-1"
                            aria-hidden="true"
                          >
                            {/*begin::Modal dialog*/}
                            <div className="modal-dialog modal-dialog-centered modal-lg">
                              {/*begin::Modal content*/}
                              <div className="modal-content rounded">
                                {/*begin::Modal header*/}
                                <div className="modal-header">
                                  {/*begin::Close*/}
                                  <h4>Change Employee Password</h4>
                                  {/*end::Close*/}
                                </div>
                                {/*end::Modal header*/}

                                {/*begin::Modal body*/}
                                <div className="modal-body p-4">
                                  <div className="row">
                                    <div className="col">
                                      <label className="form-label required">
                                        New Password
                                      </label>
                                      <input
                                        value={employee?.password ?? ""}
                                        onChange={(e) => {
                                          setEmployee({
                                            ...employee,
                                            password: e.target.value,
                                          });
                                          setError({ ...error, password: "" });
                                        }}
                                        id="employee_password"
                                        className="form-control"
                                        placeholder="New Password"
                                        autoComplete="off"
                                        type="password"
                                      />
                                      {error.password !== undefined &&
                                        error.password !== "" ? (
                                        <small className="text-danger mt-2">
                                          {error.password}
                                        </small>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                    <div className="col">
                                      <label className="form-label required">
                                        Confirm Password
                                      </label>
                                      <input
                                        value={employee?.confirmPassword ?? ""}
                                        onChange={(e) => {
                                          setEmployee({
                                            ...employee,
                                            confirmPassword: e.target.value,
                                          });
                                          setError({
                                            ...error,
                                            confirmPassword: "",
                                          });
                                        }}
                                        id="employee_confirm_password"
                                        className="form-control"
                                        placeholder="Confirm Password"
                                        autoComplete="off"
                                        type="password"
                                      />
                                      {error.confirmPassword !== undefined &&
                                        error.confirmPassword !== "" ? (
                                        <small className="text-danger mt-2">
                                          {error.confirmPassword}
                                        </small>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                  </div>

                                  {/*begin::Actions*/}
                                  <div className="d-flex flex-row-fluid pt-12">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-secondary me-3"
                                      onClick={() => {
                                        setPopup("");
                                        setEmployee({});
                                      }}
                                    >
                                      Close
                                    </button>

                                    <button
                                      onClick={changeEmployeePassword}
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                    >
                                      Update
                                    </button>
                                  </div>
                                  {/*end::Actions*/}
                                </div>
                                {/*end::Modal body*/}
                              </div>
                              {/*end::Modal content*/}
                            </div>
                            {/*end::Modal dialog*/}
                          </div>
                        ) : (
                          ""
                        )}
                        {/*end::Modal - Change Employee Password*/}
                      </div>
                      {/*end::Col*/}
                    </div>
                    {/*end::Row*/}
                    {/*begin::Row*/}
                    <div className="row g-5 g-lg-10 mt-4">
                      {/*begin::Col*/}
                      <div
                        className="col ps-0 mb-xl-10"
                        id="loadSubscriptions"
                      ></div>
                      {/*end::Col*/}
                    </div>
                    {/*end::Row*/}

                    {/*begin:: Wallet Row*/}
                    <WalletBalance
                      id={id}
                      balance={data?.balanceInt ?? 0}
                      currency={data?.currency}
                      freezedBalance={data?.freezedBalanceInt ?? 0}
                      setAlertConfig={setAlertConfig}
                      fetchMerchantDetails={fetchMerchantDetails}
                      bumpCreditRefresh={bumpCreditRefresh}
                    />
                    {/*end:: Walllet Row*/}

                    <PermissionGate required={`view_merchant_credit`} show={false}>
                      <CreditPanel
                        id={id}
                        currency={data?.currency}
                        setAlertConfig={setAlertConfig}
                        fetchMerchantDetails={fetchMerchantDetails}
                        creditRefreshKey={creditRefreshKey}
                      />
                    </PermissionGate>

                    <PermissionGate required={`update_merchant_order_scan_restriction`} show={false}>
                      {/*begin::Order Scan Restriction Row*/}
                      <OrderScanRestriction orderScanRestriction={orderScanRestriction} setOrderScanRestriction={setOrderScanRestriction} changeMerchantOrderScanRestriction={changeMerchantOrderScanRestriction} />
                      {/*end::Order Scan Restriction Row*/}
                    </PermissionGate>

                    <PermissionGate required={`manage_merchant_preferred_driver_setting`} show={false}>
                      {/*begin::Preferred Driver Setting Row*/}
                      <PreferredDriverSetting
                        preferredDriverSettings={data?.preferredDriverSettings}
                        employees={employees}
                        saveMerchantPreferredDriverSetting={saveMerchantPreferredDriverSetting}
                      />
                      {/*end::Preferred Driver Setting Row*/}
                    </PermissionGate>

                    {/* begin:: Merchant API Key Row */}
                    <MerchantAPIKey id={id} />
                    {/* end:: Merchant API Key Row */}

                    {/* begin:: Branch API Keys Row */}
                    <APIKeys id={id} />
                    {/* end:: Branch API Keys Row */}

                    {/* begin:: Ordable Branch Keys Row */}
                    <OrdableBranchKeys id={id} />
                    {/* end:: Ordable Branch Keys Row */}

                    {/*begin:: Transactions Row*/}
                    <div className="row g-5 g-lg-10 mt-4">
                      {/*begin::Col*/}
                      <div className="col p-0">
                        <div className="d-flex w-100 justify-content-between mb-5">
                          <div className="d-flex text-start text-dark fw-bold m-0 fs-3">
                            Transactions
                          </div>
                          <div className="form-check form-switch form-check-custom fv-row">
                            <input
                              className="form-check-input w-45px h-30px"
                              type="checkbox"
                              checked={showTransaction}
                              onChange={() =>
                                setShowTransaction(!showTransaction)
                              }
                            />
                            <label className="form-check-label"></label>
                          </div>
                        </div>
                        {/*begin::Tables Widget 3*/}
                        {showTransaction && (
                          <TransactionTable
                            merchantId={id}
                            balance={data?.balance}
                            showTransaction={showTransaction}
                          />
                        )}
                        {/*end::Tables Widget 3*/}
                      </div>
                      {/*end::Col*/}
                    </div>
                    {/*end:: Transactions Row*/}
                  </div>
                  {/*end::Post*/}
                </div>
              </>
            </PermissionGate>
          </div>
          <Footer />
        </div>
        {/*end::Wrapper*/}
      </div>
      {/*end::Page*/}
    </div>
  );
};

export default MerchantDetails;